/** Pure, side-effect-free schema/config helpers for the schema-driven grid.
 *
 * No React, no MUI — extracted verbatim (behaviour-preserving) from
 * `../AssistedConfigEditor.tsx` and generalized to take the schema's
 * `properties` map as a parameter instead of closing over a module import.
 */
import type { ConfigRow, SchemaProp } from './types';

// ---------------------------------------------------------------------------
// Enum labels
// ---------------------------------------------------------------------------

/** Look up an enum value's display label by matching against `prop.enum`. */
export function enumLabel(prop: SchemaProp, value: unknown): string | undefined {
  if (!prop.enum || !prop['x-enum-labels']) return undefined;
  const idx = prop.enum.findIndex((e) => e === value);
  return idx >= 0 ? prop['x-enum-labels'][idx] : undefined;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export function getDefaultValue(prop: SchemaProp): unknown {
  if (prop.default !== undefined) return prop.default;
  if (prop.type === 'boolean') return false;
  if (prop.type === 'integer' || prop.type === 'number') return 0;
  if (prop.type === 'string') return '';
  if (prop.type === 'array') return [];
  if (prop.type === 'object') {
    if (!prop.properties) return {};
    const obj: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(prop.properties)) {
      if (v.default !== undefined) obj[k] = v.default;
    }
    return obj;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Immutable nested mutations
// ---------------------------------------------------------------------------

/** Parse a dotted path with optional `[N]` list-index segments into an array
 *  of string keys and numeric indices: `"a.b[0].c"` → `["a", "b", 0, "c"]`;
 *  chained indices are supported (`"a[0][1]"` → `["a", 0, 1]`).
 *
 *  Config keys never contain `[`, so a bracket unambiguously starts an index
 *  group. Segments without brackets pass through verbatim (pure dot-paths
 *  behave exactly as `path.split('.')` did). Malformed bracket groups (e.g.
 *  `"a[x]"`) throw — the UI never produces them. */
export function splitPathSegments(path: string): (string | number)[] {
  const out: (string | number)[] = [];
  for (const part of path.split('.')) {
    const bracket = part.indexOf('[');
    if (bracket === -1) {
      out.push(part);
      continue;
    }
    const name = part.slice(0, bracket);
    const rest = part.slice(bracket);
    if (!/^(\[\d+\])+$/.test(rest)) {
      throw new Error(`splitPathSegments: malformed segment "${part}" in "${path}"`);
    }
    if (name) out.push(name);
    for (const m of rest.matchAll(/\[(\d+)\]/g)) {
      out.push(Number(m[1]));
    }
  }
  return out;
}

/** Container to descend into when the current value is missing: `[]` when the
 *  next segment is an index (creating a list for a missing key), `{}` for a
 *  dict key — exactly the old `?? {}` behaviour for dot chains. A non-list
 *  value in the way of an index segment throws (the UI never produces it). */
function childFor(existing: unknown, nextSeg: string | number): unknown {
  if (typeof nextSeg === 'number') {
    if (existing == null) return [];
    if (!Array.isArray(existing)) {
      throw new Error(`setNestedValue: cannot index [${nextSeg}] into a non-list value`);
    }
    return existing;
  }
  return existing ?? {};
}

function setBySegments(node: unknown, segments: (string | number)[], value: unknown): unknown {
  const [head, ...tail] = segments;
  const nextSeg = tail[0];
  if (typeof head === 'number') {
    if (!Array.isArray(node)) {
      throw new Error(`setNestedValue: expected a list at segment [${head}]`);
    }
    if (head < 0 || head > node.length) {
      throw new Error(
        `setNestedValue: index ${head} out of range (list length ${node.length})`,
      );
    }
    const next = [...node];
    next[head] =
      nextSeg === undefined ? value : setBySegments(childFor(node[head], nextSeg), tail, value);
    return next;
  }
  if (!head) return node;
  const obj = (node ?? {}) as Record<string, unknown>;
  if (nextSeg === undefined) {
    return { ...obj, [head]: value };
  }
  return { ...obj, [head]: setBySegments(childFor(obj[head], nextSeg), tail, value) };
}

/** Set a value at a dotted `path`, returning a new object (immutable).
 *
 *  `[N]` index segments are supported ("auth_methods[0].domain"): traversed
 *  arrays are copied (`[...arr]`), the index must satisfy 0 ≤ i ≤ length
 *  (i === length appends), and a missing key whose next segment is an index
 *  is created as `[]`. Out-of-range indices or a non-list value in the way
 *  throw an Error. Pure dot-paths behave exactly as before. */
export function setNestedValue(
  config: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  return setBySegments(config, splitPathSegments(path), value) as Record<string, unknown>;
}

function removeBySegments(node: unknown, segments: (string | number)[]): unknown {
  const [head, ...tail] = segments;
  const nextSeg = tail[0];
  if (typeof head === 'number') {
    // Never the final segment here (guarded in removeNestedKey) — traverse
    // through the list element immutably (the array is copied, not mutated).
    if (!Array.isArray(node) || head < 0 || head >= node.length) return node;
    const next = [...node];
    next[head] = removeBySegments(node[head], tail);
    return next;
  }
  if (!head) return node;
  if (node == null || typeof node !== 'object' || Array.isArray(node)) return node;
  const obj = node as Record<string, unknown>;
  if (nextSeg === undefined) {
    const next = { ...obj };
    delete next[head];
    return next;
  }
  const childRaw = obj[head];
  if (typeof nextSeg === 'number') {
    if (!Array.isArray(childRaw)) return obj;
  } else if (childRaw == null || typeof childRaw !== 'object' || Array.isArray(childRaw)) {
    return obj;
  }
  return { ...obj, [head]: removeBySegments(childRaw, tail) };
}

/** Remove `path` from the config object, returning a new object.
 *  Removes the key entirely (not just sets it to undefined). Immutable.
 *
 *  `[N]` index segments are supported for TRAVERSAL only ("a[0].b" removes
 *  key `b` inside list element 0, copying the array immutably). The final
 *  segment must be a dict key — removing a list *element* is not supported:
 *  when the final segment is an index this is a no-op returning the input.
 *  Parent containers are never pruned (matching the existing dict-chain
 *  behaviour), and arrays are never pruned either. */
export function removeNestedKey(
  config: Record<string, unknown>,
  path: string,
): Record<string, unknown> {
  const segments = splitPathSegments(path);
  if (typeof segments[segments.length - 1] === 'number') return config;
  return removeBySegments(config, segments) as Record<string, unknown>;
}

/** Remove a batch of dotted paths at once, returning a new object (immutable). */
export function removePaths(
  config: Record<string, unknown>,
  paths: string[],
): Record<string, unknown> {
  return paths.reduce((acc, p) => removeNestedKey(acc, p), config);
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export function formatType(prop: SchemaProp | null): string {
  if (!prop) return 'unknown';
  if (prop.enum) return `${prop.type ?? 'enum'} enum`;
  if (prop['x-unit']) return `${prop.type} (${prop['x-unit']})`;
  return prop.type ?? 'unknown';
}

/** JSON-stringify a value safely; never returns undefined ("—" for nullish). */
export function safePreview(value: unknown, max = 60): string {
  if (value === undefined) return '—';
  let s: string | undefined;
  try {
    s = JSON.stringify(value);
  } catch {
    s = String(value);
  }
  if (s === undefined) return '—';
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

// ---------------------------------------------------------------------------
// Row builder
// ---------------------------------------------------------------------------

export interface BuildRowsOptions {
  /** Filter rows by key/description substring match. */
  search?: string;
  /** Set of expanded group row ids (dot-joined paths). */
  expanded?: Set<string>;
  /** Top-level key prefixes to hide from DISPLAY only. Never mutates the
   *  config value object — hidden keys stay in the config, they are only
   *  filtered out of the rendered "unknown" section. A key is hidden when it
   *  exactly equals a prefix or starts with `${prefix}.`. */
  hiddenKeyPrefixes?: string[];
}

/** Flatten a schema + config into a list of grid rows.
 *
 *  Schema-defined keys present in the config come first (alphabetically,
 *  with nested groups expanded in-place as indented children), followed by
 *  unknown top-level keys (present in config but absent from the schema).
 *
 *  Generalized from the original two-bucket `{ schema, unknown }` return into
 *  a single flat array: schema rows first, unknown rows last — preserving the
 *  original ordering and grouping semantics.
 */
export function buildRows(
  schemaProps: Record<string, SchemaProp>,
  config: Record<string, unknown>,
  opts: BuildRowsOptions = {},
): ConfigRow[] {
  const search = opts.search ?? '';
  const expanded = opts.expanded ?? new Set<string>();
  const hiddenKeyPrefixes = opts.hiddenKeyPrefixes ?? [];

  const schemaRows: ConfigRow[] = [];
  const unknownRows: ConfigRow[] = [];
  const lc = search.toLowerCase();

  // A hidden prefix hides any key that starts with it (e.g. `_cp_` hides
  // `_cp_meta`) — reserved/managed keys are filtered from DISPLAY only; the
  // caller's config value is never mutated here.
  const isHidden = (key: string) => hiddenKeyPrefixes.some((p) => key.startsWith(p));

  const matchesSearch = (key: string, prop: SchemaProp | null) =>
    !search ||
    key.toLowerCase().includes(lc) ||
    (prop?.description ?? '').toLowerCase().includes(lc);

  // Schema-defined keys present in config — sorted alphabetically
  const schemaEntries = Object.entries(schemaProps).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  for (const [key, prop] of schemaEntries) {
    if (!(key in config)) continue;
    const value = config[key];
    // A schema-typed object is expandable whenever the live value is a
    // non-null plain object — even if the schema doesn't declare child
    // `properties` (e.g. `additionalProperties: true` blobs). In that case
    // the children come purely from the config blob and render as "unknown"
    // children (no per-key schema description).
    // A key with a custom widget (x-widget) is edited by that widget as a whole,
    // not expanded into child rows.
    const isGroup =
      !prop['x-widget'] &&
      prop.type === 'object' &&
      value != null &&
      typeof value === 'object' &&
      !Array.isArray(value);

    const rowId = key;
    const row: ConfigRow = {
      id: rowId,
      path: [key],
      depth: 0,
      key,
      schema: prop,
      value,
      isGroup,
      isExpanded: expanded.has(rowId),
      isUnknown: false,
    };

    const childMatches =
      Boolean(search) &&
      isGroup &&
      (() => {
        const schemaKids = prop.properties ? Object.keys(prop.properties) : [];
        const configKids = Object.keys((value as Record<string, unknown>) ?? {});
        const all = new Set<string>([...schemaKids, ...configKids]);
        return [...all].some(
          (ck) =>
            ck.toLowerCase().includes(lc) ||
            (prop.properties?.[ck]?.description ?? '').toLowerCase().includes(lc),
        );
      })();

    if (!matchesSearch(key, prop) && !childMatches) continue;
    schemaRows.push(row);

    // Children for expanded groups — sorted alphabetically
    if (isGroup && (expanded.has(rowId) || (search && childMatches))) {
      const childConfig = (value as Record<string, unknown>) ?? {};
      const childProps = prop.properties ?? {};
      const childEntries = Object.entries(childProps).sort(([a], [b]) =>
        a.localeCompare(b),
      );
      for (const [ck, cp] of childEntries) {
        const childId = `${key}.${ck}`;
        if (search && !matchesSearch(ck, cp)) continue;
        schemaRows.push({
          id: childId,
          path: [key, ck],
          depth: 1,
          key: ck,
          schema: cp,
          value: childConfig[ck],
          isGroup: false,
          isExpanded: false,
          isUnknown: false,
        });
      }
      // Unknown children — keys present in the config but not declared by the
      // parent's schema properties (the only path that fires for schema
      // objects with `additionalProperties: true` and no `properties`).
      const unknownChildKeys = Object.keys(childConfig)
        .filter((ck) => !(ck in childProps))
        .sort((a, b) => a.localeCompare(b));
      for (const ck of unknownChildKeys) {
        const childId = `${key}.${ck}`;
        if (search && !ck.toLowerCase().includes(lc)) continue;
        schemaRows.push({
          id: childId,
          path: [key, ck],
          depth: 1,
          key: ck,
          schema: null,
          value: childConfig[ck],
          isGroup: false,
          isExpanded: false,
          isUnknown: true,
        });
      }
    }
  }

  // Unknown top-level keys — sorted alphabetically. Keys matching a hidden
  // prefix are filtered from DISPLAY only (they remain in the config value).
  const unknownTopKeys = Object.keys(config)
    .filter((k) => !(k in schemaProps) && !isHidden(k))
    .sort((a, b) => a.localeCompare(b));
  for (const key of unknownTopKeys) {
    if (search && !key.toLowerCase().includes(lc)) continue;
    unknownRows.push({
      id: `__unknown.${key}`,
      path: [key],
      depth: 0,
      key,
      schema: null,
      value: config[key],
      isGroup: false,
      isExpanded: false,
      isUnknown: true,
    });
  }

  return [...schemaRows, ...unknownRows];
}
